import { Button, Modal, ModalHeader } from '@fountain/fountain-ui-components';
import { DialogContent, Typography } from '@material-ui/core';
import { CheckCircleOutlineIcon, Icon } from '@fountain/ripple/icon';
import complianceAppIcon from 'images/compliance-app-icon.svg';
import complianceModalPreview from 'images/compliance-modal-preview.png';
import React, { FC } from 'react';
import { useIntl } from 'react-intl';

import { CueCtaButton } from 'components/CueCtaButton';
import { usePostHog } from 'hooks/usePostHog';

import {
  CUE_COMPLIANCE_UPSELL_MODAL,
  MODAL_CLOSE_ACTION,
} from '../analytics-events';
import messages from './messages';
import { MODAL_WIDTH, useStyles } from './styles';

/**
 * The Compliance marketing page the secondary CTA opens.
 *
 * TODO(CMP-1903): the final URL is not chosen yet. One line to change.
 */
export const COMPLIANCE_MARKETING_URL = 'https://www.fountain.com/compliance';

export interface ComplianceUpsellModalProps {
  openingTitle: string;
  stageTitle: string;
  open: boolean;
  onClose: () => void;
}

/**
 * The message the Start free trial CTA sends to Cue.
 *
 * Deliberately not a react-intl message: it is an instruction to Cue, not UI
 * copy, so it must not go through Smartling. The Workflow Management skill runs
 * the upsell procedure only on the exact `#compliance-upsell` token, and a
 * translated variant could alter the token or drift from the skill's wording.
 * `hideMessage` hides the current chat bubble only; the persisted text shows in
 * chat history, so it must read as a sentence.
 */
export const buildDiscoverCuePrompt = (
  openingTitle: string,
  stageTitle: string,
): string =>
  `#compliance-upsell Set up Compliance on the "${openingTitle}" workflow, starting from the "${stageTitle}" stage.`;

const CHECK_ROWS = [
  messages.checkReject,
  messages.checkReview,
  messages.checkOcr,
] as const;

// The Compliance pitch the Discover button on ComplianceUpsellStageCard opens.
// "Start free trial" is the card's old Discover action — the same Cue dispatch
// with the same message — so the moved CueCtaButton keeps that behaviour
// rather than reimplementing it here.
//
// That button also owns the modal's view event: it captures one on mount, and
// the card mounts this component only while it is open, so the mount is the
// open. A second capture here would double every open.
export const ComplianceUpsellModal: FC<ComplianceUpsellModalProps> = ({
  openingTitle,
  stageTitle,
  open,
  onClose,
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const posthog = usePostHog();

  const handleStartFreeTrial = () => {
    onClose();
  };

  const handleDiscoverCompliance = () => {
    posthog.capture(CUE_COMPLIANCE_UPSELL_MODAL.closeEvent, {
      action: MODAL_CLOSE_ACTION.marketingLink,
      cta_id: CUE_COMPLIANCE_UPSELL_MODAL.ctaId,
    });
    window.open(COMPLIANCE_MARKETING_URL, '_blank', 'noopener,noreferrer');
  };

  // Both the × and a backdrop click route through here: MUI's Dialog calls
  // `onClose` for the backdrop, and the × is the header's own button.
  const handleClose = () => {
    posthog.capture(CUE_COMPLIANCE_UPSELL_MODAL.closeEvent, {
      action: MODAL_CLOSE_ACTION.dismiss,
      cta_id: CUE_COMPLIANCE_UPSELL_MODAL.ctaId,
    });
    onClose();
  };

  return (
    <Modal
      ariaLabelledBy="compliance-upsell-modal-header"
      maxWidth={MODAL_WIDTH}
      onClose={handleClose}
      open={open}
    >
      <ModalHeader
        ariaLabelledBy="compliance-upsell-modal-header"
        onClose={handleClose}
        showIcon={false}
      >
        {intl.formatMessage(messages.header)}
      </ModalHeader>

      <DialogContent className={classes.content} dividers>
        <div className={classes.body}>
          <div className={classes.previewPanel}>
            <img
              alt={intl.formatMessage(messages.previewAlt)}
              className={classes.previewImage}
              src={complianceModalPreview}
            />
          </div>

          <div className={classes.pitch}>
            <div className={classes.eyebrowRow}>
              <img
                alt=""
                className={classes.eyebrowIcon}
                src={complianceAppIcon}
              />
              <Typography className={classes.eyebrow} variant="body2">
                {intl.formatMessage(messages.eyebrow)}
              </Typography>
            </div>

            <Typography className={classes.heading} variant="h5">
              {intl.formatMessage(messages.heading)}
            </Typography>

            <Typography className={classes.bodyText} variant="body2">
              {intl.formatMessage(messages.body)}
            </Typography>

            <div className={classes.priceRow}>
              <Typography className={classes.price} variant="body1">
                {intl.formatMessage(messages.price)}
              </Typography>
              <Typography className={classes.priceUnit} variant="body2">
                {intl.formatMessage(messages.priceUnit)}
              </Typography>
            </div>

            <ul className={classes.checks}>
              {CHECK_ROWS.map(message => (
                <li className={classes.check} key={message.id}>
                  <Icon
                    icon={CheckCircleOutlineIcon}
                    size="lg"
                    color="inherit"
                    className={classes.checkIcon}
                  />
                  <Typography className={classes.checkText} variant="body2">
                    {intl.formatMessage(message)}
                  </Typography>
                </li>
              ))}
            </ul>

            <div className={classes.trialPanel}>
              <div className={classes.trialCopy}>
                <Typography className={classes.trialCopyLine} variant="body2">
                  {intl.formatMessage(messages.freeDocuments)}
                </Typography>
                <Typography className={classes.trialCopyLine} variant="body2">
                  {intl.formatMessage(messages.noCommitment)}
                </Typography>
              </div>

              <CueCtaButton
                analytics={CUE_COMPLIANCE_UPSELL_MODAL}
                hideMessage
                message={buildDiscoverCuePrompt(openingTitle, stageTitle)}
                onClick={handleStartFreeTrial}
              >
                {intl.formatMessage(messages.startFreeTrial)}
              </CueCtaButton>
            </div>

            <Button
              className={classes.discoverCompliance}
              onClick={handleDiscoverCompliance}
              type="text"
            >
              {intl.formatMessage(messages.discoverCompliance)}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Modal>
  );
};
